import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Loading } from "@/components/Loading";
import { RequireAuth } from "@/auth/RequireAuth";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { LoginPage } from "@/pages/LoginPage";

const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const TemplatesPage = lazy(() =>
  import("@/pages/TemplatesPage").then((m) => ({ default: m.TemplatesPage })),
);
const TemplateDetailPage = lazy(() =>
  import("@/pages/TemplateDetailPage").then((m) => ({
    default: m.TemplateDetailPage,
  })),
);
const ConfigurationsPage = lazy(() =>
  import("@/pages/ConfigurationsPage").then((m) => ({
    default: m.ConfigurationsPage,
  })),
);
const ConfigurationDetailPage = lazy(() =>
  import("@/pages/ConfigurationDetailPage").then((m) => ({
    default: m.ConfigurationDetailPage,
  })),
);
const ConfigurationEditorPage = lazy(() =>
  import("@/pages/ConfigurationEditorPage").then((m) => ({
    default: m.ConfigurationEditorPage,
  })),
);
const CharactersPage = lazy(() =>
  import("@/pages/CharactersPage").then((m) => ({ default: m.CharactersPage })),
);
const StoryPlanPage = lazy(() =>
  import("@/pages/StoryPlanPage").then((m) => ({ default: m.StoryPlanPage })),
);
const GenerationRunsPage = lazy(() =>
  import("@/pages/GenerationRunsPage").then((m) => ({
    default: m.GenerationRunsPage,
  })),
);
const BookVersionsPage = lazy(() =>
  import("@/pages/BookVersionsPage").then((m) => ({
    default: m.BookVersionsPage,
  })),
);
const StudioPage = lazy(() =>
  import("@/pages/StudioPage").then((m) => ({ default: m.StudioPage })),
);
const JobsPage = lazy(() =>
  import("@/pages/JobsPage").then((m) => ({ default: m.JobsPage })),
);

function withLayout(element: React.ReactNode) {
  return (
    <RequireAuth>
      <Layout>
        <Suspense fallback={<Loading />}>{element}</Suspense>
      </Layout>
    </RequireAuth>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/", element: withLayout(<DashboardPage />) },
  { path: "/templates", element: withLayout(<TemplatesPage />) },
  {
    path: "/templates/:templateId",
    element: withLayout(<TemplateDetailPage />),
  },
  { path: "/configurations", element: withLayout(<ConfigurationsPage />) },
  {
    path: "/configurations/:configurationId",
    element: withLayout(<ConfigurationDetailPage />),
  },
  {
    path: "/configurations/:configurationId/edit",
    element: withLayout(<ConfigurationEditorPage />),
  },
  {
    path: "/configurations/:configurationId/characters",
    element: withLayout(<CharactersPage />),
  },
  {
    path: "/configurations/:configurationId/story-plan",
    element: withLayout(<StoryPlanPage />),
  },
  {
    path: "/configurations/:configurationId/runs",
    element: withLayout(<GenerationRunsPage />),
  },
  {
    path: "/configurations/:configurationId/studio",
    element: withLayout(<StudioPage />),
  },
  {
    path: "/configurations/:configurationId/book",
    element: withLayout(<BookVersionsPage />),
  },
  { path: "/jobs", element: withLayout(<JobsPage />) },
  { path: "*", element: <NotFoundPage /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
